// domain/kasir — uang masuk/keluar per HARI untuk tutup kasir. Murni, tanpa akses DB.
//
// ATURAN INTI: uang dihitung per PERTAMBAHAN, bukan per total tiket.
// Satu tiket bisa menyentuh kas di tiga hari berbeda:
//   • DP saat terima servis        (tglISO)
//   • sisa saat serah terima       (tglAmbilISO)
//   • pelunasan tempo belakangan   (lunasAt)
// Kalau hari serah-terima dihitung `biaya` penuh, DP-nya terhitung DUA KALI.
//
// Ini SENGAJA memakai kas AKTUAL, bukan `omzetTiket` (yang baru diakui setelah verifikasi Finance).
// Tujuannya berbeda: ini menghitung isi laci sore hari, bukan mengakui pendapatan.
// Lihat [[omzet-cash-basis-decision]] — aturan omzet tidak tersentuh oleh modul ini.

import { isPiutangCara } from './money';

export type Metode = 'Tunai' | 'Transfer' | 'QRIS' | 'Lainnya' | 'Belum dicatat';
export const METODE: Metode[] = ['Tunai', 'Transfer', 'QRIS', 'Lainnya', 'Belum dicatat'];

export type Sumber = 'serah' | 'dp' | 'tempo' | 'refund' | 'komisi';

export interface KasTiket {
  no: string;
  outlet?: string | null;
  custNama?: string;
  unit?: string;
  biaya?: number;
  biayaSimpan?: number;
  dp?: number;
  lunas?: boolean;
  caraBayar?: string | null;   // metode saat serah terima
  dpVia?: string | null;       // metode saat DP diterima
  lunasVia?: string | null;    // metode saat pelunasan tempo
  tglISO?: string | null;      // tgl terima servis (DP masuk)
  tglAmbilISO?: string | null; // tgl unit keluar
  lunasAt?: string | null;     // kapan ditandai lunas (ISO)
  refundAmount?: number;       // refund terverifikasi
  refundAt?: string | null;    // kapan refund diverifikasi
  refundVia?: string | null;   // metode PENGEMBALIAN (beda dari cara bayar tiket!)
  arsipKeu?: boolean;
}

export interface KasRow { no: string; sumber: Sumber; metode: Metode; jumlah: number; custNama: string; unit: string; campuran: boolean;
  /** Teks cara bayar APA ADANYA ("Transfer BCA"). `metode` sudah dinormalkan sehingga dua
   *  rekening bank tak bisa dibedakan lagi — padahal itulah yang dicocokkan ke mutasi bank. */
  caraAsli?: string | null }

export interface KasHarian {
  masuk: KasRow[];
  keluar: KasRow[];              // refund (nilai positif; artinya uang KELUAR)
  perMetode: Record<string, number>; // masuk − keluar, per metode
  totalMasuk: number;
  totalKeluar: number;
  tempoBaru: number;             // unit keluar hari itu dgn tempo → piutang baru, BUKAN kas
}
// "Pelunasan tanpa jejak tanggal" SENGAJA tak dihitung di sini: nilainya akan bergantung pada tiket
// mana yang kebetulan ikut terambil query harian → angka berubah-ubah tanpa alasan. Hitung terpisah
// dari daftar lengkapnya (repo.tempoTanpaTanggal) memakai kasPelunasanTempo().

const n = (v: unknown): number => (typeof v === 'number' && isFinite(v) ? v : 0);
/**
 * Tanggal LOKAL toko dari sebuah instan. `lunas_at`/`refund.verifiedAt` disimpan UTC, sedangkan
 * `tgl_iso`/`tgl_ambil_iso` sudah berupa hari-bisnis lokal. Memotong `slice(0,10)` dari ISO UTC
 * membuat satu layar memakai DUA definisi "hari": pelunasan pukul 00:00–06:59 WIB akan jatuh ke
 * hari kemarin — hari yang mungkin sudah ditutup & diverifikasi, sehingga uangnya tak pernah
 * direkonsiliasi. Kolom `date` (panjang 10) dibiarkan apa adanya.
 */
export const TZ_TOKO = 'Asia/Jakarta';
const ymd = (iso: string | null | undefined): string => {
  if (!iso) return '';
  if (iso.length <= 10) return iso;                       // kolom `date` — sudah lokal
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA', { timeZone: TZ_TOKO }); // en-CA → YYYY-MM-DD
};
// Pakai predikat BERSAMA dari domain/money — jangan menulis regex sendiri di sini.
// Regex lokal `/tempo/i` dulu tak mengenali opsi "Piutang", sehingga pelunasan piutang pelanggan
// biasa tak pernah muncul di Tutup Kasir: uangnya masuk laci tapi hilang dari laporan.
const isTempo = isPiutangCara;
/** Refund yang TIDAK mengeluarkan uang tunai/bank (opsi di modul refund). */
const REFUND_NONKAS = /potong piutang|voucher|credit/i;

/** Normalisasi metode; yang kosong/tak dikenal → "Belum dicatat" (jujur, jangan ditebak jadi Tunai). */
export function metodeOf(v: string | null | undefined): Metode {
  const s = (v ?? '').toLowerCase();
  if (!s) return 'Belum dicatat';
  if (s.includes('tunai') || s.includes('cash')) return 'Tunai';
  if (s.includes('transfer') || s.includes('bank')) return 'Transfer';
  if (s.includes('qris')) return 'QRIS';
  if (s.includes('tempo')) return 'Belum dicatat'; // tempo bukan metode terima uang
  return 'Lainnya';
}

/** Tagihan penuh satu tiket (servis + biaya simpan). */
export const tagihan = (t: KasTiket): number => n(t.biaya) + n(t.biayaSimpan);

/**
 * Uang yang diterima SAAT SERAH TERIMA = tagihan − DP (DP sudah diterima lebih dulu di hari intake).
 * Tempo → Rp 0 masuk kas (jadi piutang). Belum lunas → Rp 0.
 */
export function kasSaatSerah(t: KasTiket): number {
  if (!t.lunas || isTempo(t.caraBayar)) return 0;
  return Math.max(0, tagihan(t) - n(t.dp));
}

/** Pelunasan tempo = tagihan − DP, diterima pada `lunasAt` (bukan di hari serah terima). */
export function kasPelunasanTempo(t: KasTiket): number {
  if (!t.lunas || !isTempo(t.caraBayar)) return 0;
  return Math.max(0, tagihan(t) - n(t.dp));
}

/**
 * Hitung kas satu hari untuk satu cabang.
 * `tgl` = 'YYYY-MM-DD' lokal. Tiket arsip-keuangan dikecualikan (tutup buku).
 */
/** `ekstraKeluar` = uang keluar non-tiket hari itu (mis. komisi TUNAI yang disetujui) — sudah
 *  difilter tanggal+cabang oleh pemanggil; ikut perMetode & totalKeluar agar laci cocok. */
export function kasHarian(tickets: KasTiket[], tgl: string, outlet?: string | null, ekstraKeluar: KasRow[] = []): KasHarian {
  const masuk: KasRow[] = [], keluar: KasRow[] = [];
  let tempoBaru = 0;
  const inScope = (t: KasTiket): boolean => !t.arsipKeu && (!outlet || (t.outlet ?? '') === outlet);

  for (const t of tickets) {
    if (!inScope(t)) continue;
    const meta = { no: t.no, custNama: t.custNama ?? '—', unit: t.unit ?? '—' };
    // Tiket ber-DP berpotensi metode campuran (DP tunai, pelunasan transfer) — ditandai agar
    // tidak diam-diam salah; model tiket belum bisa menyatakan dua metode dalam satu transaksi.
    const campuran = n(t.dp) > 0;

    // 1) DP diterima di hari terima servis.
    if (ymd(t.tglISO) === tgl && n(t.dp) > 0) {
      masuk.push({ ...meta, sumber: 'dp', metode: metodeOf(t.dpVia), caraAsli: t.dpVia, jumlah: n(t.dp), campuran: false });
    }

    // 2) Sisa diterima di hari unit keluar.
    if (ymd(t.tglAmbilISO) === tgl) {
      const j = kasSaatSerah(t);
      if (j > 0) masuk.push({ ...meta, sumber: 'serah', metode: metodeOf(t.caraBayar), caraAsli: t.caraBayar, jumlah: j, campuran });
      if (isTempo(t.caraBayar)) tempoBaru += Math.max(0, tagihan(t) - n(t.dp));
    }

    // 3) Pelunasan tempo — di hari uangnya masuk, bukan di hari unit keluar.
    const jt = kasPelunasanTempo(t);
    // Tanpa jejak tanggal → tak dimasukkan ke hari mana pun (dilaporkan terpisah, lihat catatan di atas).
    if (jt > 0 && t.lunasAt && ymd(t.lunasAt) === tgl) masuk.push({ ...meta, sumber: 'tempo', metode: metodeOf(t.lunasVia), caraAsli: t.lunasVia, jumlah: jt, campuran });

    // 4) Refund keluar di hari verifikasinya — pakai metode PENGEMBALIAN, bukan cara bayar tiket.
    //    (Tiket dibayar QRIS lalu uang dikembalikan TUNAI dari laci → yang berkurang laci, bukan QRIS.)
    //    Refund non-kas (potong piutang / voucher) tak mengeluarkan rupiah → jangan dihitung;
    //    kalau dihitung, ia menutupi kekurangan kas yang asli.
    if (n(t.refundAmount) > 0 && ymd(t.refundAt) === tgl && !REFUND_NONKAS.test(t.refundVia ?? '')) {
      keluar.push({ ...meta, sumber: 'refund', metode: metodeOf(t.refundVia), caraAsli: t.refundVia, jumlah: n(t.refundAmount), campuran: false });
    }
  }

  keluar.push(...ekstraKeluar);

  const perMetode: Record<string, number> = {};
  for (const r of masuk) perMetode[r.metode] = (perMetode[r.metode] ?? 0) + r.jumlah;
  for (const r of keluar) perMetode[r.metode] = (perMetode[r.metode] ?? 0) - r.jumlah;

  return {
    masuk, keluar, perMetode,
    totalMasuk: masuk.reduce((a, r) => a + r.jumlah, 0),
    totalKeluar: keluar.reduce((a, r) => a + r.jumlah, 0),
    tempoBaru,
  };
}

/** Selisih hitungan fisik vs seharusnya, per metode. Positif = fisik LEBIH. */
export function selisih(expect: Record<string, number>, actual: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of new Set([...Object.keys(expect), ...Object.keys(actual)])) {
    out[k] = n(actual[k]) - n(expect[k]);
  }
  return out;
}

/** Total setoran (ke bank / tunai diserahkan) — dijumlah terpisah per jenis. */
export interface Setoran { jenis: 'bank' | 'tunai'; tujuan?: string; jumlah: number; ref?: string }
export const totalSetoran = (rows: Setoran[], jenis?: 'bank' | 'tunai'): number =>
  rows.filter((r) => !jenis || r.jenis === jenis).reduce((a, r) => a + n(r.jumlah), 0);

// ---------------------------------------------------------------------------
// Analisa selisih — "di mana letak tidak-sinkronnya?"
// ---------------------------------------------------------------------------
export interface Petunjuk {
  kode: 'setoran' | 'belum_bayar' | 'metode_kosong' | 'metode_campuran' | 'tempo_tanpa_tanggal';
  judul: string;
  penjelasan: string;
  jumlah: number;              // nilai yang bisa menjelaskan selisih
  arah: 'lebih' | 'kurang';    // efeknya pada uang fisik: 'kurang' = fisik jadi lebih sedikit
  nota: string[];
  cocokPersis: boolean;        // nilainya SAMA PERSIS dgn selisih → hampir pasti ini penyebabnya
}

/**
 * Cari penyebab selisih dari data yang ada — bukan menebak, tapi menyodorkan tersangka berikut notanya.
 * Yang nilainya sama persis dengan selisih ditandai `cocokPersis` dan diurut paling atas:
 * dalam praktik kasir, kecocokan angka persis hampir selalu berarti itulah penyebabnya.
 *
 * `selisihTotal` = fisik − seharusnya. Negatif berarti uang fisik KURANG.
 */
export function analisaSelisih(
  selisihTotal: number,
  kas: KasHarian,
  setoran: Setoran[],
  unitKeluarBelumBayar: { no: string; jumlah: number }[],
  tanpaTanggal = 0,
): Petunjuk[] {
  const out: Petunjuk[] = [];
  const sama = (v: number): boolean => v > 0 && Math.abs(v - Math.abs(selisihTotal)) < 1;

  // 1) Uang sudah disetor → laci memang berkurang. Penyebab paling sering "selisih" yang bukan selisih.
  const setoranTunai = totalSetoran(setoran, 'tunai') + totalSetoran(setoran, 'bank');
  if (setoranTunai > 0 && selisihTotal < 0) {
    out.push({
      kode: 'setoran', judul: 'Uang sudah disetor', jumlah: setoranTunai, arah: 'kurang',
      penjelasan: 'Uang yang sudah disetor ke bank / diserahkan tidak lagi ada di laci. Kalau hitungan fisik dilakukan SETELAH menyetor, jumlah ini menjelaskan kekurangannya.',
      nota: setoran.map((s) => `${s.jenis === 'bank' ? '🏦' : '💵'} ${s.tujuan ?? '—'}`), cocokPersis: sama(setoranTunai),
    });
  }

  // 2) HP keluar tapi belum ditandai lunas → uangnya bisa saja SUDAH diterima, hanya belum dicatat.
  const belum = unitKeluarBelumBayar.reduce((a, x) => a + x.jumlah, 0);
  if (belum > 0) {
    out.push({
      kode: 'belum_bayar', judul: 'Unit keluar tapi belum ditandai lunas', jumlah: belum, arah: 'lebih',
      penjelasan: 'Unit sudah keluar tapi pembayarannya belum dicatat. Bila pelanggan sebenarnya sudah membayar, uangnya ada di laci tetapi tidak terhitung "seharusnya" — inilah sebabnya fisik jadi lebih banyak.',
      nota: unitKeluarBelumBayar.map((x) => x.no), cocokPersis: sama(belum),
    });
  }

  // 3) Metode tak tercatat → uang benar masuk, tapi tak jelas ke laci atau ke rekening.
  const kosong = kas.masuk.filter((r) => r.metode === 'Belum dicatat');
  const kosongTot = kosong.reduce((a, r) => a + r.jumlah, 0);
  if (kosongTot > 0) {
    out.push({
      kode: 'metode_kosong', judul: 'Uang masuk tanpa metode tercatat', jumlah: kosongTot, arah: 'lebih',
      penjelasan: 'Uang ini masuk, tetapi tidak tercatat diterima via tunai/transfer/QRIS — jadi tak bisa dipastikan masuk laci atau rekening. Periksa notanya lalu perbaiki cara bayarnya.',
      nota: [...new Set(kosong.map((r) => r.no))], cocokPersis: sama(kosongTot),
    });
  }

  // 4) Tiket ber-DP: DP dan pelunasan bisa beda metode, sedangkan tiket hanya menyimpan satu.
  const campur = kas.masuk.filter((r) => r.campuran);
  const campurTot = campur.reduce((a, r) => a + r.jumlah, 0);
  if (campurTot > 0) {
    out.push({
      kode: 'metode_campuran', judul: 'Tiket ber-DP (metode bisa campuran)', jumlah: campurTot, arah: 'lebih',
      penjelasan: 'Tiket ini punya DP. DP dan pelunasannya bisa diterima lewat cara berbeda (mis. DP tunai, sisanya transfer), padahal tiket hanya menyimpan satu cara bayar — jadi pembagian per metode bisa meleset sebesar ini.',
      nota: [...new Set(campur.map((r) => r.no))], cocokPersis: sama(campurTot),
    });
  }

  // 5) Pelunasan tanpa jejak tanggal → uangnya nyata, harinya tak diketahui.
  if (tanpaTanggal > 0) {
    out.push({
      kode: 'tempo_tanpa_tanggal', judul: 'Pelunasan tempo tanpa tanggal (data lama)', jumlah: tanpaTanggal, arah: 'lebih',
      penjelasan: 'Pelunasan ini tercatat lunas tetapi tanggal uang masuknya tidak diketahui (data sebelum fitur ini ada), jadi sengaja tidak dimasukkan ke hari mana pun.',
      nota: [], cocokPersis: sama(tanpaTanggal),
    });
  }

  // Yang nilainya cocok persis naik ke atas — itu yang paling mungkin menjawab "di mana letaknya".
  return out.sort((a, b) => (Number(b.cocokPersis) - Number(a.cocokPersis)) || (b.jumlah - a.jumlah));
}


// ── Ringkasan riwayat penutupan (laporan harian → bulanan) ────────────────────

export interface PenutupanHari {
  tgl: string;
  outlet: string | null;
  expect: Record<string, number>;
  actual: Record<string, number>;
  setoran: { jenis: string; jumlah: number }[];
  closed_at?: string | null;
  verified_at?: string | null;
}

export interface BarisPenutupan {
  tgl: string; outlet: string | null;
  seharusnya: number; fisik: number; selisih: number; setoran: number;
  /** Sudah dihitung fisik? Hari tanpa hitungan fisik TIDAK boleh dianggap "selisih 0". */
  dihitung: boolean;
  status: 'terverifikasi' | 'ditutup' | 'draft';
}

/**
 * Satu baris ringkas per hari.
 *
 * Selisih dihitung HANYA dari metode/akun yang benar-benar diisi hitungan fisiknya. Kalau semua
 * metode ikut, hari yang belum dihitung akan tampil "selisih −Rp sekian" seolah uangnya hilang —
 * padahal hanya belum dihitung. Itu jenis laporan yang membuat orang berhenti percaya angkanya.
 */
export function baris1Penutupan(p: PenutupanHari): BarisPenutupan {
  const kunci = Object.keys(p.actual ?? {});
  const seharusnya = kunci.reduce((a, k) => a + (p.expect?.[k] ?? 0), 0);
  const fisik = kunci.reduce((a, k) => a + (p.actual?.[k] ?? 0), 0);
  const setoran = (p.setoran ?? []).reduce((a, s) => a + (s.jumlah ?? 0), 0);
  return {
    tgl: p.tgl, outlet: p.outlet,
    seharusnya, fisik, selisih: fisik - seharusnya, setoran,
    dihitung: kunci.length > 0,
    status: p.verified_at ? 'terverifikasi' : p.closed_at ? 'ditutup' : 'draft',
  };
}

export interface RingkasPenutupan {
  hari: number; dihitung: number; terverifikasi: number;
  seharusnya: number; fisik: number; selisih: number; setoran: number;
  /** Hari yang selisihnya tidak nol — yang perlu dilihat pemilik lebih dulu. */
  hariSelisih: number;
}

export function ringkasPenutupan(rows: BarisPenutupan[]): RingkasPenutupan {
  const dihitung = rows.filter((r) => r.dihitung);
  return {
    hari: rows.length,
    dihitung: dihitung.length,
    terverifikasi: rows.filter((r) => r.status === 'terverifikasi').length,
    seharusnya: dihitung.reduce((a, r) => a + r.seharusnya, 0),
    fisik: dihitung.reduce((a, r) => a + r.fisik, 0),
    selisih: dihitung.reduce((a, r) => a + r.selisih, 0),
    setoran: rows.reduce((a, r) => a + r.setoran, 0),
    hariSelisih: dihitung.filter((r) => r.selisih !== 0).length,
  };
}
